import { ComponentType, lazy, Suspense, useEffect, useMemo } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';

import { useIsAuthenticated, AuthenticationTemplate } from '@/shared/lib/auth';

import Alert from '../components/alert';
import LastRouterLocation from '../components/last-router-location';
import Menu from '../components/menu';
import { MODALS, MODAL_NAMES, defaultGuid, ROLES_WITH_NO_ACCESS_TO_TASKS_REDIRECT } from '../constants';
import ModalOverlay from '../modal-overlay/modal-overlay';
import { useDispatch, useSelector } from 'react-redux';
import { setCustomUrlTree } from '../actions/app_action';
import DashboardTitle from '../components/dashboard-title';
import { useBlockerModal } from 'hooks/modals/use-blocker.modal';
import { useRefreshIndicators } from 'hooks/use-refresh-indicators/use-refresh-indicators.ts';
import cloneDeep from 'lodash/cloneDeep';
import { defaultUrlTree } from 'components/breadcrumbs-panel-dashboard/breadcrumbs-panel-dashboard';
import { ComposedFormContextList } from './index.composed-context';
import { useGetRoutes } from './get-routes';
import { lazyLoadingRetryOnFail } from './utils';
import { useIsUserHaveRole } from 'hooks/use-is-user-have-role';
import { PAGES, UNAUTHORIZED_PAGES } from 'common/enums';
import { PageContainer } from './dashboard/page-container';
import GlobalSnackbar from './global-snackbar';
import Modal from '../components/modal';
import PageNotFound from '../components/page-not-found';
import { useOnPageChange } from './hooks/use-on-page-change';
import useSetUserInfoToRedux from '../hooks/react-query/use-set-user-info-to-redux';
import useUserInfo from 'hooks/react-query/use-user-info';
import { useRedirectTo } from 'hooks/use-redirect-to';
import useUnauthorized from 'hooks/use-unauthorized';
import { Text } from 'common/common-components.styled';
import { AnyObject } from 'common/interfaces';

/**
 * Lazy-loads a component that is not needed for the first render and wraps it in its own Suspense,
 * so props injected by parents (e.g. ModalOverlay's cloneElement) still reach the loaded component.
 */
const lazyComponent = (importComponent: () => Promise<{ default: ComponentType<any> }>) => {
  const LazyComponent = lazy(() => lazyLoadingRetryOnFail(importComponent));
  const LazyComponentWithSuspense = (props: AnyObject) => (
    <Suspense fallback={null}>
      <LazyComponent {...props} />
    </Suspense>
  );
  return LazyComponentWithSuspense;
};

const ComparisonForm = lazyComponent(() => import('../components/comparison-form'));
const FileViewerComponent = lazyComponent(() => import('../components/file-viewer'));
const InsuranceDialog = lazyComponent(() => import('../components/insurance-dialog/insurance-dialog-view'));
const RepairStatusPageUnauthorized = lazyComponent(
  () => import('./repair-status/unauthorized/repair-status-unauthorized')
);
const CheckoutPopup = lazyComponent(() => import('../components/checkout-popup/checkout-popup'));
const QuizComponent = lazyComponent(() => import('components/quiz-component/quiz-component'));
const ReportDamagePage = lazyComponent(() => import('./report-damage-page/report-damage-component'));
const DashboardGuidedTour = lazyComponent(() => import('components/dashboard-guided-tour/dashboard-guided-tour'));
const AfterTourPopUp = lazyComponent(() => import('components/after-tour-pop-up/after-tour-pop-up'));
const NewVehicleRequestSurvey = lazyComponent(() => import('../components/new-vehicle-request-survey'));
const SigningExternalComponent = lazyComponent(() => import('./signing-external-page/signing-external-conponent'));
const ReleaseExternalPage = lazyComponent(() => import('./release-external-page'));
const VehicleHandoverProtocolUnauthorizedPage = lazyComponent(() => import('./vehicle-handover-protocol-unauthorized'));

const Pages = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(state => state['app'].user);

  const isNoNeededTasksTitle = useIsUserHaveRole(Object.keys(ROLES_WITH_NO_ACCESS_TO_TASKS_REDIRECT));
  const mainRoleId = user.mainRole?.id;

  const { unauthorized } = useUnauthorized();
  const { tasksRedirectTo } = useRedirectTo();
  const { getRoutes } = useGetRoutes();

  const routes = useMemo(() => getRoutes(tasksRedirectTo, user.appPages), [mainRoleId]);

  useOnPageChange();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isNoNeededTasksTitle) {
      const newUrlTree = cloneDeep(defaultUrlTree);
      newUrlTree.breadcrumbTitle = '';
      dispatch(setCustomUrlTree(newUrlTree));

      return () => {
        dispatch(setCustomUrlTree(null));
      };
    }
  }, [location.pathname, isNoNeededTasksTitle]);

  const isAuthorizedPage = useMemo(
    () =>
      !Object.values(UNAUTHORIZED_PAGES)
        .map(url => url.replaceAll('/'))
        .includes(location.pathname.replaceAll('/')),
    [location.pathname]
  );

  const stepsToFix = (
    <>
      <Text>1) Bitte logge Dich einmal bei Mobexo aus, schließe den Tab und logge Dich direkt wieder ein</Text>
      <Text>2) Ihre Konfiguration wurde geändert und sie haben keinen Zugriff mehr</Text>
      <Text>3) es könnte ein Fehler bei Mobexo vorliegen</Text>
    </>
  );

  const notFoundConfig = {
    pageTitle: '404 - Seite nicht gefunden',
    imageSrc: '/assets/images/page-not-found.png',
    subTitle: 'Error 404',
    mainTitle: 'Seite nicht gefunden!',
    description: 'Die von dir gesuchte Seite konnte nicht gefunden werden.',
    buttons: [
      {
        buttonText: 'Zurück zur Startseite',
        action: () => history.push(PAGES.TASKS)
      },
      {
        buttonText: 'Mobexo kontaktieren',
        action: () => (window.location.href = 'mailto:fuhrpark@mobexo.de'),
        showCopyEmailIcon: true,
        email: 'fuhrpark@mobexo.de'
      }
    ]
  };

  const roleLessConfig = {
    pageTitle: DashboardTitle,
    imageSrc: '/assets/images/role-less-image.png',
    subTitle: 'Error Rechte',
    mainTitle: 'Du hast keinen Zugriff.',
    description: (
      <>
        <Text bottom='10'>
          Du besitzt aktuell keine Rechte für die Inhalte dieser Seite. Bitte kontaktiere Mobexo damit sie deine Rechte
          prüfen.
        </Text>
        {stepsToFix}
      </>
    ),
    buttons: [
      {
        buttonText: 'Mobexo kontaktieren',
        action: () => (window.location.href = 'mailto:fuhrpark@mobexo.de'),
        showCopyEmailIcon: true,
        email: 'fuhrpark@mobexo.de'
      }
    ]
  };

  const noCompanyConfig = {
    pageTitle: DashboardTitle,
    imageSrc: '/assets/images/role-less-image.png',
    subTitle: 'Error Rechte',
    mainTitle: 'Du hast keinen Zugriff.',
    description: (
      <>
        <Text bottom='10'>Ihnen ist keine Firma zugeordnet. Bitte kontaktieren Sie Mobexo.</Text>
        {stepsToFix}
      </>
    ),
    buttons: [
      {
        buttonText: 'Mobexo kontaktieren',
        action: () => (window.location.href = 'mailto:fuhrpark@mobexo.de'),
        showCopyEmailIcon: true,
        email: 'fuhrpark@mobexo.de'
      }
    ]
  };

  const noPagesConfig = {
    pageTitle: DashboardTitle,
    imageSrc: '/assets/images/role-less-image.png',
    subTitle: 'Error Rechte',
    mainTitle: 'Du hast keinen Zugriff.',
    description: (
      <>
        <Text bottom='10'>Leider hast du keine Produkte. Bitte kontaktiere Mobexo.</Text>
        {stepsToFix}
      </>
    ),
    buttons: [
      {
        buttonText: 'Mobexo kontaktieren',
        action: () => (window.location.href = 'mailto:fuhrpark@mobexo.de'),
        showCopyEmailIcon: true,
        email: 'fuhrpark@mobexo.de'
      }
    ]
  };

  const unauthorizedConfig = {
    pageTitle: DashboardTitle,
    imageSrc: '/assets/images/page-not-found.png',
    subTitle: '',
    mainTitle: 'Error Rechte',
    description:
      'Du besitzt aktuell keine Rechte für die Inhalte dieser Seite. Bitte kontaktiere Mobexo, damit deine Rechte überprüft werden können.',
    buttons: [
      {
        buttonText: 'Zurück zur Startseite',
        action: () => history.push(PAGES.TASKS)
      },
      {
        buttonText: 'Mobexo kontaktieren',
        action: () => (window.location.href = 'mailto:fuhrpark@mobexo.de'),
        showCopyEmailIcon: true,
        email: 'fuhrpark@mobexo.de'
      }
    ]
  };

  const isAuthenticated = useIsAuthenticated();

  useRefreshIndicators();
  useUserInfo(!Object.values(UNAUTHORIZED_PAGES).includes(location.pathname) && isAuthenticated);
  useSetUserInfoToRedux(isAuthorizedPage);
  useBlockerModal(location.pathname, isAuthorizedPage);

  return (
    <ComposedFormContextList>
      <LastRouterLocation>
        <Menu />
        <Modal name={MODALS.general_comparison_form}>
          <ModalOverlay type={MODAL_NAMES.comparison_form}>
            <ComparisonForm />
          </ModalOverlay>
        </Modal>
        <Modal name={MODALS.alert}>
          <ModalOverlay type={MODAL_NAMES.alert}>
            <Alert />
          </ModalOverlay>
        </Modal>
        <Modal name={MODALS.additional_dialog}>
          <ModalOverlay type={MODAL_NAMES.additional_dialog}>
            <Alert />
          </ModalOverlay>
        </Modal>
        <Modal name={MODALS.insurance_dialog}>
          <ModalOverlay type={MODAL_NAMES.insurance_dialog}>
            <InsuranceDialog />
          </ModalOverlay>
        </Modal>
        <Modal name={MODALS.file_viewer}>
          <ModalOverlay type={MODAL_NAMES.file_viewer}>
            <FileViewerComponent />
          </ModalOverlay>
        </Modal>
        <Modal name={MODALS.checkout}>
          <ModalOverlay type={MODAL_NAMES.checkout}>
            <CheckoutPopup />
          </ModalOverlay>
        </Modal>

        <Switch>
          {user.roles.length > 0 && (
            <Route path='/' exact>
              <Redirect to={PAGES.TASKS} />
            </Route>
          )}
          <Route key='reparaturstatus' path={UNAUTHORIZED_PAGES.REPAIRED_STATUS}>
            <RepairStatusPageUnauthorized />
          </Route>
          <Route key='signing' path={UNAUTHORIZED_PAGES.SURVEY}>
            <NewVehicleRequestSurvey />
          </Route>
          <Route key='umfrage' path={UNAUTHORIZED_PAGES.OPINION_POLL}>
            <QuizComponent />
          </Route>
          <Route key='schaden-melden' path={UNAUTHORIZED_PAGES.REPORT_DAMAGE}>
            <ReportDamagePage />
          </Route>

          <Route key='signing' path={UNAUTHORIZED_PAGES.SIGNING}>
            <SigningExternalComponent />
          </Route>

          <Route key='release' path={UNAUTHORIZED_PAGES.RELEASE}>
            <ReleaseExternalPage />
          </Route>

          <Route key='handover-protocol' path={UNAUTHORIZED_PAGES.VEHICLE_HANDOVER_PROTOCOL}>
            <VehicleHandoverProtocolUnauthorizedPage />
          </Route>

          <AuthenticationTemplate>
            <PageContainer>
              <Switch>
                {unauthorized && (
                  <Route path='/'>
                    <PageNotFound config={unauthorizedConfig} />
                  </Route>
                )}
                {!user.appPages.length && (
                  <Route path='/'>
                    <PageNotFound config={noPagesConfig} />
                  </Route>
                )}
                {!user.roles.length && (
                  <Route path='/'>
                    <PageNotFound config={roleLessConfig} />
                  </Route>
                )}
                {user.companyId === defaultGuid && (
                  <Route path='/'>
                    <PageNotFound config={noCompanyConfig} />
                  </Route>
                )}
                {routes.map(route => (
                  <Route key={route.path} path={route.path} exact>
                    {(route.hasAccess ? route.hasAccess : false) ? route.component : <Redirect to={route.redirectTo} />}
                  </Route>
                ))}
                <Route path={PAGES.SIGN_IN}>
                  <Redirect to={PAGES.TASKS} />
                </Route>
                <Route path='/'>
                  <PageNotFound config={notFoundConfig} />
                </Route>
              </Switch>
            </PageContainer>
          </AuthenticationTemplate>
        </Switch>

        <GlobalSnackbar />

        <DashboardGuidedTour isSideMenuVisible={window.innerWidth > 1200} />
        <AfterTourPopUp />
      </LastRouterLocation>
    </ComposedFormContextList>
  );
};

export default Pages;
